import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import anime from "animejs";
import { unwrapResult } from "@reduxjs/toolkit";
import { useDialog } from "app/hook";
import { AppDispatch, RootState } from "app/store";
import { DialogType, RestrictedFeatureSource } from "app/types";
import I18n from "common/i18n";
import { SpinModal } from "common/elements";
import { TutorialArrow } from "common/assets";
import { btnDelay, useScale, usePlaySound, shouldApplyGuestRestrictions } from "common/utils";
import { AccountType } from "features/user/types";
import { ProjectType } from "features/creator/types";
import { createProject } from "features/creator/api";
import { selectAllProjects } from "features/creator/slice";
import { Overlay } from "features/creator/tutorial/top/stages/Layout";
import { ProjectLimitDialog, CreateProjectModal } from "features/creator/elements";
import { usePlan, usePlanLimits, PlanType, PLAN_LIMITS } from "features/subscription";

const Step = (props: { back: () => void; next: () => void; close: () => void }) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const { next, close } = props;
  const handleDialog = useDialog();
  const plan = usePlan();
  const limits = usePlanLimits();
  const dispatch = useDispatch<AppDispatch>();
  const [create, setCreate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [guidance, setGuidance] = useState(false);
  const projects = useSelector(selectAllProjects);
  const user = useSelector((state: RootState) => state.user.active);
  const userProjects = projects.filter(
    (project) =>
      project.author_uid === user.uid && !project.deleted_at && project.type === ProjectType.BASIC,
  );
  const [panelRect, setPanelRect] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  useEffect(() => {
    setTimeout(() => {
      const panel = document.getElementById("user-project-panel-new-app-btn");
      const rect = panel.getBoundingClientRect();
      setPanelRect({
        x: rect.x - 25 * scale,
        y: rect.y - 25 * scale,
        width: rect.width + 50 * scale,
        height: rect.height + 50 * scale,
      });
    }, 300);
  }, [scale]);

  const handleClick = () => {
    play();
    btnDelay(() => {
      // OLD TRIALユーザー判定（2025-11-02以前に作成されたGUESTユーザー）
      const isOldTrialUser =
        plan === PlanType.TRIAL &&
        user.account_type === AccountType.GUEST &&
        !shouldApplyGuestRestrictions(user.created_at);

      // 制限値を計算（OLD TRIALはFREEと同じ制限）
      const projectLimit = isOldTrialUser
        ? PLAN_LIMITS[PlanType.FREE].projects.limit
        : limits.projects.limit;

      // 無制限プランはチェックスキップ
      if (limits.projects.unlimited) {
        setCreate(true);
        return;
      }

      // 制限に達している場合
      if (userProjects.length >= projectLimit) {
        // TRIAL/FREE/STANDARD: RestrictedFeatureDialog表示
        if (plan === PlanType.TRIAL || plan === PlanType.FREE) {
          handleDialog({
            type: DialogType.RESTRICTED_FEATURE,
            value: true,
            args: { source: RestrictedFeatureSource.PROJECT_LIMIT },
          });
          close();
          return;
        }
        // EDUCATION/PREMIUM/BUILDER: ProjectLimitDialog表示（通知のみ）
        setGuidance(true);
        return;
      }

      setCreate(true);
    });
  };

  // プラン別ダイアログのアクションハンドラ（EDUCATION/STANDARD用）
  // TRIAL/FREEは直接リダイレクトするのでこの関数は呼ばれない
  const handleGuidanceAction = () => {
    play();
    setGuidance(false);
    close();
  };

  const handleCloseGuidance = () => {
    play();
    setGuidance(false);
    close();
  };
  const handleCreateProject = (name: string, type: ProjectType) => {
    play();
    setLoading(true);
    btnDelay(() => {
      dispatch(
        createProject({
          uid: user.uid,
          name,
          type,
        }),
      )
        .then(unwrapResult)
        .then((result) => {
          anime({
            targets: document.getElementById(
              `user-app-project-container-${result.project.project_id}`,
            ),
            scale: [1.3, 1],
            opacity: [0, 1],
            duration: 400,
            easing: "easeInOutQuad",
          });
          next();
        });
    });
  };
  const handleCreateProjectCancel = () => {
    play();
    btnDelay(() => setCreate(false));
  };

  return (
    <>
      {loading && <SpinModal />}

      <ProjectLimitDialog open={guidance} type="app" close={handleCloseGuidance} />

      <CreateProjectModal
        zIndex={20000}
        visible={create}
        name={`${I18n.t("MSG_CREATOR_CREATE_APP_NAME_PREFIX")}${userProjects.length + 1}`}
        type={ProjectType.BASIC}
        create={handleCreateProject}
        close={handleCreateProjectCancel}
      />

      <Overlay area={panelRect} onClick={handleClick}>
        <div
          className="absolute z-50"
          style={{
            width: 108 * scale,
            height: 105 * scale,
            transform: `translate(${panelRect.x - 100 * scale}px, ${
              panelRect.y + 40 * scale
            }px) rotate(-45deg)`,
          }}
        >
          <TutorialArrow />
        </div>
      </Overlay>
    </>
  );
};

export default Step;
